import { deleteFetcher, getFetcher, postFetcher } from '@/api/fetchers';
import { DropDownList } from '@/components/DropDownList/DropDownList';
import { FormModal } from '@/components/Modal/FormModal';
import { goodNotification } from '@/components/Notification/Notification';
import type {
  ClientAttachment,
  ClientAttachments,
} from '@/schemas/API/GET/client/attachment/response';
import {
  displayError,
  displayErrorOnUseState,
} from '@/utils/error/errorHandler';
import { FileInput, Paper } from '@mantine/core';
import { formatDateAndTime } from '@/utils/Date';
import { IconDownload, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';

export const AttachmentPage = (): JSX.Element => {
  const { serviceId } = useParams<{ serviceId: string }>();
  interface FormData {
    file: File | null;
  }

  const [formData, setFormData] = useState<FormData>({
    file: null,
  });
  const { data: attachmentList, mutate: mutateList } =
    useSWR<ClientAttachments>(`/services/${serviceId}/attachments`, getFetcher);

  const [attachmentIdToDelete, setAttachmentIdToDelete] = useState<
    number | null
  >(null);

  const { data: selectedAttachment } = useSWR<ClientAttachment>(
    attachmentIdToDelete
      ? `/services/${serviceId}/attachments/${attachmentIdToDelete}`
      : null,
    getFetcher
  );

  const [fileError, setFileError] = useState('');

  const [
    isConfirmDeleteServiceAttachmentModalOpen,
    setIsConfirmDeleteServiceAttachmentModalOpen,
  ] = useState(false);

  const [isNewAttachmentModalOpen, setIsNewAttachmentModalOpen] =
    useState(false);

  const openNewAttachmentModal = () => {
    setIsNewAttachmentModalOpen(true);
  };

  const closeNewAttachmentModal = () => {
    setIsNewAttachmentModalOpen(false);
    setFormData({ file: null });
    setFileError('');
    mutateList();
  };

  const openConfirmDeleteModal = () => {
    setIsConfirmDeleteServiceAttachmentModalOpen(true);
  };

  const closeConfirmDeleteModal = () => {
    setIsConfirmDeleteServiceAttachmentModalOpen(false);
    mutateList();
  };

  const handleChange = (file: File | null): void => {
    setFileError('');
    setFormData({ file });
  };

  const createAttachment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setFileError('');

    if (formData.file === null || formData.file === undefined) {
      setFileError('Nahrajte soubor, prosím');
      return;
    }

    const sendFormData = new FormData();
    sendFormData.append('Content-Type', 'multipart/form-data');
    sendFormData.append('name', formData.file.name);
    sendFormData.append('file', formData.file);
    sendFormData.append('type', 'OTHER');
    sendFormData.append('Accept', 'application/json');

    try {
      await postFetcher(`/services/${serviceId}/attachments`, sendFormData);
      goodNotification('Příloha je uložená');
      closeNewAttachmentModal();
    } catch (err) {
      displayErrorOnUseState(err, [{ field: 'file', setState: setFileError }]);
    }
  };

  async function deleteAttachment(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await deleteFetcher(
        `/services/${serviceId}/attachments/${attachmentIdToDelete}`
      );
      goodNotification('Úspěch!', 'Příloha je smazaná');
    } catch (err) {
      displayError(err);
    } finally {
      closeConfirmDeleteModal();
    }
  }

  function downloadAttachment(attachment: ClientAttachment): void {
    window.open(attachment.url, '_blank');
  }

  const formattedAttachments = attachmentList?.content.map(item => {
    return {
      ...item,
      createdAt: formatDateAndTime(item.createdAt),
    };
  });

  return (
    <Paper withBorder radius="md" className="p-4 flex-1 overflow-auto">
      <DropDownList
        title="Přílohy"
        list={formattedAttachments}
        createNewItem={openNewAttachmentModal}
        createNewItemButtonText="Přidat"
        itemToolBar={[
          {
            index: 0,
            function: downloadAttachment,
            icon: <IconDownload />,
            onHoverColor: 'blue',
          },
          {
            index: 1,
            function: (item: ClientAttachment) => {
              setAttachmentIdToDelete(item.id);
              openConfirmDeleteModal();
            },
            icon: <IconTrash />,
          },
        ]}
        loading={attachmentList === undefined}
        displayAttributes={[{ index: 0, attribute: 'createdAt' }]}
      ></DropDownList>
      <FormModal
        opened={isConfirmDeleteServiceAttachmentModalOpen}
        onClose={closeConfirmDeleteModal}
        onConfirm={deleteAttachment}
        confirmText="Smazat"
      >
        <div className="text-lg text-center">
          Opravdu si přejete smazat přílohu{' '}
          <span className="font-bold italic">{selectedAttachment?.name}</span> ?
        </div>
      </FormModal>
      <FormModal
        confirmButtonColor="green"
        opened={isNewAttachmentModalOpen}
        onClose={closeNewAttachmentModal}
        onConfirm={createAttachment}
        title={`Nová přiloha`}
        confirmText="Uložit"
      >
        <FileInput
          label="Nahrat soubor"
          placeholder="Vybrat soubor"
          onChange={handleChange}
          error={fileError}
          radius="md"
          size="md"
        />
      </FormModal>
    </Paper>
  );
};

export {};
